import { useEffect, useState } from 'react';
import {
  TextField,
  Icon,
  Divider,
  Button,
  Checkbox,
  HorizontalGrid,
  ButtonGroup,
  HorizontalStack,
} from '@shopify/polaris';
import { SearchMinor } from '@shopify/polaris-icons';
import { hydrateFilters } from '../../../services/productService';
import Filter from '../../UI/Products/FilterSelect';
import { handleSearchParams } from '../../../helpers/handleSearchParams';

const defaultFilters = {
  s: {
    name: 'Buscar',
  },
  genders: {
    name: 'Género',
  },
  categories: {
    name: 'Categorías',
  },
  subcategories: {
    name: 'Subcategorías',
  },
  seasons: {
    name: 'Temporada',
  },
  years: {
    name: 'Año',
  },
};

/**
 * @param {boolean} showStockFilter  muestra el check "incluir productos sin stock".
 *
 * Es opt-in a propósito: este componente lo comparten tres pantallas
 * (`ViewProducts`, `ViewDeposit` y `ViewLosses`), y las dos últimas parsean sus
 * filtros con `parseStockFilters`, una whitelist que descarta los parámetros
 * desconocidos en silencio. Allí el control aparecería pero no haría nada.
 */
const ProductFilters = ({ onFilterSelected, showStockFilter = false }) => {
  const [filters, setFilters] = useState({});
  const [searchFilter, setSearchFilter] = useState('');
  const [timer, setTimer] = useState(null);
  // El listado excluye por defecto lo que tiene stock 0, así que un producto
  // recién dado de alta no aparecería nunca sin activar esto.
  const [includeEmpty, setIncludeEmpty] = useState(
    () => new URLSearchParams(window.location.search).get('empty') === 'true',
  );

  const handleSearchFieldChange = (value) => {
    setSearchFilter(value);
    clearTimeout(timer);
    setTimer(setTimeout(() => handleFilter({ slug: 's', value }), 500));
  };

  const handleFilter = ({ slug, value }) => {
    handleSearchParams(slug, value);
    setFilters({ ...filters, [slug]: { ...filters[slug], value } });
  };

  const handleIncludeEmptyChange = (checked) => {
    setIncludeEmpty(checked);
    handleSearchParams('empty', checked ? 'true' : '');
  };

  const clearFilters = () => {
    Object.keys(filters).forEach((k) => handleFilter({ slug: k, value: '' }));
    setFilters(Object.keys(filters).reduce((acc, k) => ({ ...acc, [k]: { ...filters[k], value: '' } }), {}));
    setSearchFilter('');
    handleIncludeEmptyChange(false);
  };

  useEffect(() => {
    hydrateFilters(defaultFilters).then((f) => {
      setFilters(f);
      setSearchFilter(f.s.value);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const filterSelection = Object.keys(filters)
      .filter((k) => filters[k].value)
      .reduce((acc, k) => ({ ...acc, [k]: filters[k].value ?? '' }), {});

    // `empty=true` le dice al back que no descarte las familias con stock 0.
    // Va fuera de `filters` porque no es un filtro de opciones: no viene de
    // /products/filters y FilterSelect reventaría al hacer options.map().
    if (showStockFilter && includeEmpty) filterSelection.empty = 'true';

    if (Object.keys(filters).length > 0) onFilterSelected(filterSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, includeEmpty]);

  return (
    <>
      <div style={{ marginBottom: '10px' }}>
        <HorizontalGrid
          columns={{
            xs: `1fr`,
            md: '1fr',
            lg: '3fr 1fr',
            xl: '5fr 3fr',
          }}
          gap={{
            xs: '4',
            md: '2',
            lg: '2',
            xl: '2',
          }}
        >
          <TextField
            prefix={<Icon source={SearchMinor} color="inkLighter" />}
            inputMode="numeric"
            autoComplete="on"
            labelHidden
            placeholder="Buscar producto"
            value={searchFilter ?? ''}
            onChange={handleSearchFieldChange}
            onClearButtonClick={() => handleSearchFieldChange('')}
            clearButton
          ></TextField>
          <ButtonGroup segmented>
            {Object.keys(filters)
              .filter((k) => k !== 's')
              .map((k) => filters[k])
              .map(
                (filter) =>
                  filter && (
                    <Filter
                      {...filter}
                      key={filter.slug}
                      defaultValue={filter.value}
                      onFilterChange={(e) => handleFilter(e)}
                    />
                  ),
              )}
          </ButtonGroup>
        </HorizontalGrid>
      </div>
      <div style={{ marginBottom: '10px' }}>
        <Divider />
      </div>
      <HorizontalStack align="space-between" blockAlign="center">
        {showStockFilter ? (
          <Checkbox
            label="Incluir productos sin stock"
            checked={includeEmpty}
            onChange={handleIncludeEmptyChange}
            helpText="Necesario para ver los productos recién dados de alta, que aún no tienen mercancía."
          />
        ) : (
          <span />
        )}
        <Button onClick={clearFilters} plain>
          Borrar filtros
        </Button>
      </HorizontalStack>
    </>
  );
};

export default ProductFilters;
